/**
 * External dependencies
 */
import type { Maybe } from '@nab/types';

/**
 * Internal dependencies
 */
import { getCssPath } from './css-path';

export function getEditableContentElement(
	el: HTMLElement
): Maybe< HTMLElement > {
	if ( ! valid( el ) ) {
		return undefined;
	}

	const candidateNode = getWidestAncestor( el );
	const contentNode = getNarrowestChild( candidateNode );
	return 'IMG' === contentNode.nodeName || contentNode.textContent?.trim()
		? contentNode
		: undefined;
}

export function isContentEditableSelector( selector: string ): boolean {
	const els = querySelectorAll( selector );
	if ( ! hasOneElement( els ) ) {
		return false;
	}
	const editableNode = getEditableContentElement( els[ 0 ] );
	return els[ 0 ] === editableNode && selector === getCssPath( els[ 0 ] );
}

// =======
// HELPERS
// =======

const VALID_TAGS = [
	'span',
	'br',
	'a',
	'strong',
	'b',
	'em',
	'i',
	'button',
	'label',
	'li',
	'nabhidden',
];
const valid = ( e: HTMLElement | null ): e is HTMLElement =>
	!! e && ! e.querySelector( `:not(${ VALID_TAGS.join( ',' ) })` );

function getWidestAncestor( el: HTMLElement ): HTMLElement {
	let current: HTMLElement = el;
	while ( valid( current.parentElement ) ) {
		current = current.parentElement;
	}
	return current;
}

function getNarrowestChild( el: HTMLElement ): HTMLElement {
	let current: HTMLElement = el;
	while (
		isHTMLElement( current.firstElementChild ) &&
		text( el ) === text( current.firstElementChild )
	) {
		current = current.firstElementChild;
	}
	return current;
}

const text = ( e: HTMLElement ) => ( e.textContent || '' ).trim();
const isHTMLElement = ( e: Element | null ): e is HTMLElement => !! e;

const hasOneElement = < T >( a: ReadonlyArray< T > ): a is Readonly< [ T ] > =>
	a.length === 1;

function querySelectorAll( s: string ): ReadonlyArray< HTMLElement > {
	try {
		return Array.from( document.querySelectorAll< HTMLElement >( s ) );
	} catch ( _ ) {
		return [];
	}
}
